import { z } from "zod";
import { apiListSchema } from "./list";
import {
  numToReservationRegionEnum,
  ReservationRegion,
  reservationRegionEnumToNumber,
} from "./enums";

//? DTO
export const CreateReservationDtoSchema = z.object({
  TourLeaderId: z.number().nullish(),
  GroupLeaderId: z.number(),
  State: z.number().nullish(),
  Number: z.string().nullish(),
  AgencyReservationNumber: z.string().nullish(),
  TourStandart: z.number().nullish(),
  TourAgencyId: z.number().nullish(),
  Begin: z.string().nullish(),
  PaymentDate: z.string().nullish(),
  End: z.string().nullish(),
  OwnerId: z.number().nullish(),
  HeadCount: z.number().nullish(),
  BikeCount: z.number().nullish(),
  WorkerId: z.number().nullish(),
  Details: z.string().nullish(),
  Rwgpslinks: z.string().nullish(),
  Tfylinks: z.string().nullish(),
  CustomLinks: z.string().nullish(),
  ExpensesMap: z.string().nullish(),
  Price: z.number().nullish(),
  Balance: z.number().nullish(),
  OperationRegion: z.number().nullish(),
  TravefyRequired: z.boolean().nullish(),
  Rwgpsrequired: z.boolean().nullish(),
  Tags: z.string().nullish(),
  DocumentationSentDate: z.string().nullish(),
  BookingDate: z.string().nullish(),
  ConfirmedDate: z.string().nullish(),
  Gpxrequired: z.boolean().nullish(),
  Gpxlinks: z.string().nullish(),
  Feedbackrequired: z.boolean().nullish(),
  Feedbacklinks: z.string().nullish(),
  Dropboxrequired: z.boolean().nullish(),
  Dropboxlinks: z.string().nullish(),
});
export type CreateReservationDto = z.infer<typeof CreateReservationDtoSchema>;

export const ReservationDtoSchema = CreateReservationDtoSchema.extend({
  Id: z.number(),
});
export type ReservationDto = z.infer<typeof ReservationDtoSchema>;
export const reservationDtoListSchema = apiListSchema(ReservationDtoSchema);
//? Model
export enum ReservationState {
  Pending = "Pending",
  Confirmed = "Confirmed",
  Canceled = "Canceled",
  NoShow = "NoShow",
  OnGoing = "OnGoing",
  Completed = "Completed",
  Refunded = "Refunded",
}

export const reservationStateEnumToNumber: Record<ReservationState, number> = {
  Pending: 0,
  Confirmed: 1,
  Canceled: 2,
  NoShow: 3,
  OnGoing: 4,
  Completed: 5,
  Refunded: 6,
};

const numToReservationStateEnum = Object.entries(
  reservationStateEnumToNumber,
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  ReservationState
>;

export const reservationStatesAsArray = Object.values(ReservationState);

export enum TourStandard {
  Standard = "Standard",
  HotelsChanged = "HotelsChanged",
  TailorMade = "TailorMade",
}

export const tourStandardEnumToNumber: Record<TourStandard, number> = {
  Standard: 0,
  HotelsChanged: 1,
  TailorMade: 2,
};

const numToTourStandardEnum = Object.entries(tourStandardEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {},
) as Record<number, TourStandard>;

export const tourStandardAsArray = Object.values(TourStandard);

export enum ReservationTags {
  Yes = "Yes",
  No = "No",
  NotNeeded = "NotNeeded",
  Work = "Work",
}
export const reservationTagsAsArray = Object.values(ReservationTags);

const TagsSchema = z
  .object({
    reservationStates: z.array(z.nativeEnum(ReservationTags)),
  })
  .nullish();

type Tags = z.infer<typeof TagsSchema>;

export const CreateReservationSchema = z.object({
  tourAgencyId: z.number().nullish(),
  tourLeaderId: z.number().nullish(),
  groupLeaderId: z.number(),
  state: z.nativeEnum(ReservationState).nullish(),
  number: z.string().nullish(),
  begin: z.date().nullish(),
  end: z.date().nullish(),
  paymentDate: z.date().nullish(),
  ownerId: z.number().nullish(),
  headCount: z.number().nullish(),
  bikeCount: z.number().nullish(),
  workerId: z.number().nullish(),
  details: z.string().nullish(), // this was originally talked to be a list of quirks, but can't it just be a string used with a text area?
  rwgpslinks: z.array(z.string()).nullish(),
  tfylinks: z.array(z.string()).nullish(),
  customLinks: z.array(z.string()).nullish(),
  expensesMap: z.record(z.any()).nullish(),
  price: z.number().nullish(),
  balance: z.number().nullish(),
  travefyRequired: z.boolean().nullish(),
  rwgpsrequired: z.boolean().nullish(),
  operationRegion: z.nativeEnum(ReservationRegion).nullish(),
  agencyReservationNumber: z.string().nullish(),
  tourStandart: z.nativeEnum(TourStandard).nullish(),
  travefySent: z.nativeEnum(ReservationTags),
  rwgpsSent: z.nativeEnum(ReservationTags),
  gpxSent: z.nativeEnum(ReservationTags),
  documentationSentDate: z.date().nullish(),
  bookingDate: z.date().nullish(),
  confirmedDate: z.date().nullish(),
  gpxRequired: z.boolean().nullish(),
  gpxlinks: z.array(z.string()).nullish(),
  feedbackRequired: z.boolean().nullish(),
  feedbackLinks: z.array(z.string()).nullish(),
  dropboxRequired: z.boolean().nullish(),
  dropboxLinks: z.array(z.string()).nullish(),
});
export type CreateReservation = z.infer<typeof CreateReservationSchema>;

export const ReservationSchema = CreateReservationSchema.extend({
  id: z.string().min(1),
});
export type Reservation = z.infer<typeof ReservationSchema>;
//? Mappers
export function reservationFromDto(dto: ReservationDto): Reservation {
  const tags: Tags = dto.Tags
    ? TagsSchema.parse(JSON.parse(dto.Tags))
    : undefined;
  const [travefySent, rwgpsSent, gpxSent] = tags?.reservationStates ?? [];
  return {
    id: dto.Id.toString(),
    tourAgencyId: dto.TourAgencyId,
    tourLeaderId: dto.TourLeaderId,
    groupLeaderId: dto.GroupLeaderId,
    state:
      typeof dto.State === "number"
        ? numToReservationStateEnum[dto.State]
        : null,
    number: dto.Number,
    begin: dto.Begin ? new Date(dto.Begin) : null,
    end: dto.End ? new Date(dto.End) : null,
    paymentDate: dto.PaymentDate ? new Date(dto.PaymentDate) : null,
    ownerId: dto.OwnerId,
    headCount: dto.HeadCount,
    bikeCount: dto.BikeCount,
    workerId: dto.WorkerId,
    details: dto.Details,
    rwgpslinks: dto.Rwgpslinks?.split(","),
    tfylinks: dto.Tfylinks?.split(","),
    customLinks: dto.CustomLinks?.split(","),
    expensesMap: dto.ExpensesMap?.length ? JSON.parse(dto.ExpensesMap) : {},
    price: dto.Price,
    balance: dto.Balance,
    travefyRequired: dto.TravefyRequired,
    rwgpsrequired: dto.Rwgpsrequired,
    operationRegion:
      typeof dto.OperationRegion === "number"
        ? numToReservationRegionEnum[dto.OperationRegion]
        : undefined,
    agencyReservationNumber: dto.AgencyReservationNumber,
    tourStandart:
      typeof dto.TourStandart === "number"
        ? numToTourStandardEnum[dto.TourStandart]
        : undefined,
    travefySent: travefySent ?? ReservationTags.No,
    rwgpsSent: rwgpsSent ?? ReservationTags.No,
    gpxSent: gpxSent ?? ReservationTags.No,
    bookingDate: dto.BookingDate ? new Date(dto.BookingDate) : null,
    confirmedDate: dto.ConfirmedDate ? new Date(dto.ConfirmedDate) : null,
    documentationSentDate: dto.DocumentationSentDate
      ? new Date(dto.DocumentationSentDate)
      : null,
    gpxRequired: dto.Gpxrequired,
    gpxlinks: dto.Gpxlinks?.split(","),
    feedbackRequired: dto.Feedbackrequired,
    feedbackLinks: dto.Feedbacklinks?.split(","),
    dropboxRequired: dto.Dropboxrequired,
    dropboxLinks: dto.Dropboxlinks?.split(","),
  };
}

export function reservationToDto(dto: Reservation): ReservationDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createReservationToDto(dto as unknown as CreateReservation),
  };
}

export function createReservationToDto(
  dto: CreateReservation,
): CreateReservationDto {
  const tags: Tags = {
    reservationStates: [dto.travefySent, dto.rwgpsSent, dto.gpxSent],
  };
  return {
    TourAgencyId: dto.tourAgencyId,
    TourLeaderId: dto.tourLeaderId,
    GroupLeaderId: dto.groupLeaderId,
    State: dto.state ? reservationStateEnumToNumber[dto.state] : null,
    Number: dto.number,
    Begin: dto.begin?.toDateString(),
    End: dto.end?.toDateString(),
    OwnerId: dto.ownerId,
    HeadCount: dto.headCount,
    BikeCount: dto.bikeCount,
    WorkerId: dto.workerId,
    Details: dto.details,
    Rwgpslinks: dto.rwgpslinks?.join(","),
    Tfylinks: dto.tfylinks?.join(","),
    CustomLinks: dto.customLinks?.join(","),
    ExpensesMap: dto.expensesMap ? JSON.stringify(dto.expensesMap) : null,
    Price: dto.price,
    Balance: dto.balance,
    TravefyRequired: dto.travefyRequired,
    Rwgpsrequired: dto.rwgpsrequired,
    OperationRegion: dto.operationRegion
      ? reservationRegionEnumToNumber[dto.operationRegion]
      : undefined,
    PaymentDate: dto.paymentDate?.toDateString(),
    AgencyReservationNumber: dto.agencyReservationNumber,
    TourStandart: dto.tourStandart
      ? tourStandardEnumToNumber[dto.tourStandart]
      : undefined,
    Tags: JSON.stringify(tags),
    BookingDate: dto.bookingDate?.toDateString(),
    ConfirmedDate: dto.confirmedDate?.toDateString(),
    DocumentationSentDate: dto.documentationSentDate?.toDateString(),
    Gpxrequired: dto.gpxRequired,
    Gpxlinks: dto.gpxlinks?.join(","),
    Feedbackrequired: dto.feedbackRequired,
    Feedbacklinks: dto.feedbackLinks?.join(","),
    Dropboxrequired: dto.dropboxRequired,
    Dropboxlinks: dto.dropboxLinks?.join(","),
  };
}
